/** @format */

import { useState, useEffect } from "react";
import { Helmet } from "fusion-plugin-react-helmet-async";
import Footer from "../shared/components/footer";
import Navbar from "../shared/components/navbar";
import { assetUrl } from "fusion-core";
import { useLocation, useNavigate } from "fusion-plugin-react-router";
import { EditQuoteDetails, handleNext } from "../shared/utils/navigationUtils";
import { HotjarEventNames } from "../shared/utils/hotjarEventNames";
import { TrackHotjarEvent } from "../shared/utils/trackHotjarEvent";
import { clearAllStores } from "../shared/utils/storeUtils";
import BuyStep from "../features/buy/enums/BuyStep";
import PageContent from "../shared/components/PageContent";
import QuoteFormWrapper from "../shared/components/quoteFormWrapper";
import QuoteStepHeading from "../shared/components/QuoteStepHeading";
import Button from "../shared/components/Button";

const mainCss = assetUrl("../styles/main.css");
const noAccount = assetUrl(
  "../assets/images/error_no-account.png?assetUrl=true",
);
const almostThere = assetUrl(
  "../assets/images/error_almost-there.png?assetUrl=true",
);
const noQuote = assetUrl("../assets/images/error_no-quote.png?assetUrl=true");
const paymentImg = assetUrl("../assets/images/error_payment.png?assetUrl=true");

const BlockedAction = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const errorType = params.get("type");
  const headingParam = params.get("heading");
  const messageParam = params.get("message");

  const [heading, setHeading] = useState("");
  const [message, setMessage] = useState("");
  const [image, setImage] = useState("");
  const [buttonText, setButtonText] = useState("");
  const [onClick, setOnClick] = useState(() => () => {});

  const handleNoAccountError = () => {
    setHeading(
      "It looks like the Uber account you logged in with is not a driver account.",
    );
    setMessage(
      "We are unable to confirm your Driver details from Uber. To obtain a quote, you must be logged into your driver account on the device you are trying to get a quote from.",
    );
    setImage(noAccount);
    setButtonText("Go to Uber Driver Site");
    setOnClick(() => () => {
      clearAllStores();
      window.location.href = "https://www.uber.com/global/en/sign-in/";
    });
  };

  const handleAlmostThereError = () => {
    setHeading("It looks like your Uber driver account is not set up yet");
    setMessage(
      "We asked Uber for your account details but they have said your Uber account details cannot be found. This may indicate that your Uber driver account is still in the onboarding stage.",
    );
    setImage(almostThere);
    setButtonText("Go to Uber Driver Site");
    setOnClick(() => () => {
      clearAllStores();
      window.location.href = "https://www.uber.com/global/en/sign-in/";
    });
  };

  const handleNoQuoteError = () => {
    setHeading("We're sorry — no quote is available at this time");
    setMessage(
      "Unfortunately, based on the information you've provided, we're unable to offer a quote through our panel of insurers at the moment. We're constantly working to expand our panel and improve our coverage, so please check back again in the future.\nIf you think this may be a mistake, or if your details have changed, you can start a new quote at any time.",
    );
    setImage(noQuote);
    setButtonText("Edit Quote Details");
    setOnClick(() => () => {
      EditQuoteDetails();
      navigate("/taxi/quote/start");
    });
  };

  const paymentTooLong = () => {
    setHeading(
      "Payment page is taking too long to load. Please try again later.",
    );
    setMessage("We waited 20 seconds for the payment page to load.");
    setImage(paymentImg);
    setButtonText("Retry Payment");
    setOnClick(() => () => {
      handleNext(BuyStep.TB_E_PAYMENT);
      navigate(`/taxi/quote/start?code=${sessionStorage.getItem("uberCode")}`);
    });
  };

  const paymentNoLoad = () => {
    setHeading("Unable to load payment page. Please try again later.");
    setMessage("An error occurred while loading the payment page.");
    setImage(paymentImg);
    setButtonText("Retry Payment");
    setOnClick(() => () => {
      handleNext(BuyStep.TB_E_PAYMENT);
      navigate(`/taxi/quote/start?code=${sessionStorage.getItem("uberCode")}`);
    });
  };

  const paymentIframeError = () => {
    setHeading(
      headingParam || "There was an error with our payment page",
    );
    setMessage(
      messageParam || "An unexpected error occurred with the payment iframe.",
    );
    setImage(paymentImg);
    setButtonText("Retry Payment");
    setOnClick(() => () => {
      handleNext(BuyStep.TB_E_PAYMENT);
      navigate(`/taxi/quote/start?code=${sessionStorage.getItem("uberCode")}`);
    });
  };

  const handleUberAccessDenied = () => {
    setHeading(
      "It looks like you haven't provided us with access to your Uber driver account",
    );
    setMessage(
      "By providing access to your account, we can retrieve information from your Uber driver account to assist you in completing your insurance quotation, helping you get a price more quickly. We will only use this information for the purpose of generating your quotation.",
    );
    setImage(almostThere);
    setButtonText("Re-login to Driver Insurance");
    setOnClick(() => () => {
      clearAllStores();
      navigate("/");
    });
  };

  const blockedCustomer = () => {
    // Use blockedHeader/blockedDescription/blockedCategory from query params
    setHeading(headingParam || params.get("blockedHeader") || "Blocked Action");
    setMessage(
      messageParam ||
        params.get("blockedDescription") ||
        "You are currently blocked from getting a quote.",
    );
    setImage(noQuote);
    setButtonText("Go to Uber Driver Site");
    setOnClick(() => () => {
      clearAllStores();
      window.location.href = "https://www.uber.com/global/en/sign-in/";
    });
  };

  const setErrorContent = (type) => {
    switch (type) {
      case "no-account":
        handleNoAccountError();
        break;
      case "almost-there":
        handleAlmostThereError();
        break;
      case "no-quote":
        handleNoQuoteError();
        break;
      case "UberAccessDenied":
        handleUberAccessDenied();
        break;
      case "payment-too-long":
        paymentTooLong();
        break;
      case "payment-no-load":
        paymentNoLoad();
        break;
      case "iframe-error":
        paymentIframeError();
        break;
      case "blocked-customer":
        blockedCustomer();
        break;
      default:
        break;
    }
  };

  const trackErrorEvent = (type) => {
    const eventMap = {
      "no-account": HotjarEventNames.TAXI_ERROR_NO_ACCOUNT,
      "almost-there": HotjarEventNames.TAXI_ERROR_ALMOST_THERE,
      "no-quote": HotjarEventNames.TAXI_ERROR_NO_QUOTE,
    };
    if (eventMap[type]) {
      TrackHotjarEvent(eventMap[type]);
    }
  };

  useEffect(() => {
    setErrorContent(errorType);
    trackErrorEvent(errorType);
    if (headingParam) setHeading(headingParam);
    if (messageParam) setMessage(messageParam);
  }, [errorType]);

  return (
    <>
      <Helmet>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0"
        />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="stylesheet" href={mainCss} />
      </Helmet>

      <Navbar />
      <PageContent className="tw-flex tw-flex-col tw-items-center">
        <div className="tw-mx-auto tw-w-full tw-max-w-dis-card">
          <QuoteFormWrapper>
            {image && (
              <img
                src={image}
                alt=""
                className="tw-mb-5 tw-h-auto tw-w-full tw-rounded-[10px]"
              />
            )}
            <QuoteStepHeading
              title={heading}
              description={message}
              className="tw-mb-6"
              descriptionClassName="tw-whitespace-pre-line tw-text-base tw-text-dis-text-2"
            />
            <Button type="button" size="large" fullWidth onClick={onClick}>
              {buttonText}
            </Button>
          </QuoteFormWrapper>
        </div>
      </PageContent>
      <Footer />
    </>
  );
};

export default BlockedAction;
